import { useCallback, useEffect, useState } from 'react';
import { invitationService } from '@/services';
import type { InvitationWithHousehold } from '@/services/invitation.service';

export interface MyUnfinishedInvitations {
  invitations: InvitationWithHousehold[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

/**
 * Invitations addressed to the signed-in user that are not finished with them
 * yet — PENDING, or ACCEPTED with onboarding still incomplete.
 *
 * Exists because the invitee flow was reachable ONLY through the invite token
 * in the URL. A user who signed in without it — from the landing page, on a
 * second device, in another browser — was indistinguishable from a brand-new
 * user, got the full admin onboarding, and went on to create their OWN
 * household while the inviter's invitation stayed PENDING.
 *
 * ACCEPTED-but-unfinished is included because accepting seats the member
 * several steps before onboarding ends. An invitee who closes the tab
 * mid-carousel holds a real membership and an unfinished invitation; querying
 * only PENDING would find nothing for them and drop them into admin
 * onboarding.
 *
 * `enabled` is not a convenience. This must run only for someone who has NOT
 * finished onboarding: a parent can own exactly one household but may belong to
 * several others as Editor or Viewer, so an established user with a pending
 * invitation elsewhere is a normal, healthy state — pulling them back into an
 * onboarding flow because of it would be wrong. They accept additional
 * invitations from the in-app notification, which reads the PENDING-only query.
 *
 * Fails open. A lookup failure yields an empty list so the user continues to
 * the admin flow — the behaviour that existed before this hook — rather than
 * being stranded by a transient outage.
 */
export function useMyUnfinishedInvitations(
  enabled: boolean,
): MyUnfinishedInvitations {
  const [invitations, setInvitations] = useState<InvitationWithHousehold[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(
    async (isActive: () => boolean) => {
      if (!enabled) {
        if (isActive()) {
          setInvitations([]);
          setLoading(false);
          setError(null);
        }
        return;
      }
      if (isActive()) {
        setLoading(true);
        setError(null);
      }
      try {
        const rows = await invitationService.getMyUnfinishedInvitations();
        if (isActive()) setInvitations(rows);
      } catch (caught) {
        if (isActive()) {
          setInvitations([]);
          setError(
            caught instanceof Error
              ? caught
              : new Error('Unable to load your invitations'),
          );
        }
      } finally {
        if (isActive()) setLoading(false);
      }
    },
    [enabled],
  );

  const refresh = useCallback(() => load(() => true), [load]);

  useEffect(() => {
    let active = true;
    void load(() => active);
    return () => {
      active = false;
    };
  }, [load]);

  return { invitations, loading, error, refresh };
}
